/* TODO: t() */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState, useContext } from 'react';
import { View, StyleSheet } from 'react-native';
import { Link } from 'react-router-dom';

import { showSuccess } from '../App/AppAlert';

import connectData, { ConnectedProps } from '../../support/connectData';
import IndexedList from '../../support/shared/IndexedList';
import SpotDesignationActions from '../../support/SpotDesignationActions';

import Dropdown from '../uiold/Dropdown';
import LoadingIndicator from '../uiold/LoadingIndicator';
import { Text, Button, Page } from '../uiold';
import { canUser } from '../../support/shared/permissionHelpers';

import SortableDesignation from './designation/SortableDesignation';

import { Market } from '../../types/shared/Market';

type IndexNumber = number | null;

type MoveContext = {
  onToggleSelection: (index: number, value: boolean) => void;
  selectedRows: Set<number>;
};

const MultiMoveContext = React.createContext<MoveContext | null>(null);

export function useMultiMoveContext() {
  const moveContext = useContext(MultiMoveContext);
  if (moveContext == null) {
    throw new Error(
      'Can only call useMultiMoveContext from inside a MultiMoveContext.Provider',
    );
  }
  return moveContext;
}

function SpotDesignation(props: FullProps) {
  const [activeMarketID, setActiveMarketID] = useState<
    string | null | undefined
  >(null);
  const [hoveredIndex, setHoveredIndex] = useState<IndexNumber>(null);
  const [draggedIndex, setDraggedIndex] = useState<IndexNumber>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const getActiveMarket = (): Market | null | undefined => {
    let markets = props.filteredMarkets || new IndexedList([]);
    let activeMarket =
      (activeMarketID && markets.getByID(activeMarketID)) ||
      markets.getByIndex(0);
    return activeMarket;
  };

  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const onToggleSelection = (index: number, value: boolean) => {
    if (value) {
      setSelectedRows(new Set([...selectedRows, index]));
    } else {
      setSelectedRows(new Set([...selectedRows].filter((i) => i !== index)));
    }
  };

  const getPositionMap = (activeMarket: Market): Array<string> => {
    let normalizedPositionMap = [];
    let { positionMap: positionMapByMarket } = props;

    if (positionMapByMarket) {
      let positionMap = positionMapByMarket[activeMarket.id] || {};
      let numSpots = activeMarket.num_spots || 0;

      for (let i = 0; i < numSpots; i++) {
        let positionName = positionMap['' + i] || '';
        normalizedPositionMap.push(positionName);
      }
    }

    return normalizedPositionMap;
  };

  const onMarketSelect = (market: Market) => {
    setActiveMarketID(market.id);
  };

  const renderButtonToolbar = () => {
    let saveButton = [
      <Button
        title="Swap Rows"
        isDisabled={selectedRows.size !== 2}
        key={1}
        onPress={() => {
          const market = getActiveMarket();
          if (!market) {
            return;
          }
          SpotDesignationActions.swapPositionOrder(
            market,
            String([...selectedRows][0]),
            String([...selectedRows][1]),
          );
          setSelectedRows(new Set());
          showSuccess('Rows swapped successfully.');
        }}
      />,
      <Button title="Cancel" onPress={onCancel} key={2} />,
      <Button title="Save" isPrimary onPress={onSave} key={3} />,
    ];
    if (props.isSaving) {
      return <LoadingIndicator />;
    }

    return (
      <View style={{ flexDirection: 'row' }}>
        {isEditing ? null : <Button title="Edit" onPress={onEditClicked} />}
        {props.isDirty || isEditing ? saveButton : null}
      </View>
    );
  };

  const renderSpots = () => {
    let activeMarket = getActiveMarket();

    if (!activeMarket) {
      return;
    }
    let numSpots = activeMarket.num_spots || 0;
    if (numSpots === 0) {
      return (
        <Text>
          {`This market has zero spots. Please go to "Markets & Dates" tab to adjust the number of spots for this market. `}
        </Text>
      );
    }

    let positionMap = getPositionMap(activeMarket);

    return (
      <>
        <MultiMoveContext.Provider
          value={{
            onToggleSelection,
            selectedRows,
          }}
        >
          <SortableDesignation
            positionMap={positionMap}
            onMouseEnter={onHoverPositionName}
            onMouseLeave={onLeavePositionName}
            draggedIndex={draggedIndex}
            hoveredIndex={hoveredIndex}
            isEditing={isEditing}
            isDragging={isDragging}
            onSortEnd={onSortEnd}
            onSortStart={onSortStart}
            onPositionNameChange={onPositionNameChange}
          />
        </MultiMoveContext.Provider>
      </>
    );
  };

  const onSortEnd = (details: { oldIndex: number; newIndex: number }) => {
    let { oldIndex, newIndex } = details;
    let activeMarket = getActiveMarket();
    if (!activeMarket) {
      return;
    }
    SpotDesignationActions.updatePositionOrder(
      activeMarket,
      oldIndex.toString(),
      newIndex.toString(),
    );
    setDraggedIndex(null);
    setIsDragging(false);
  };

  const onSortStart = (details: { index: number }) => {
    let { index } = details;
    setDraggedIndex(index);
    setIsDragging(true);
  };

  const onEditClicked = () => {
    let activeMarket = getActiveMarket();
    if (!activeMarket) {
      return;
    }
    let positionMap = getPositionMap(activeMarket);
    positionMap.forEach((name, index) => {
      onPositionNameChange(index, name === '' ? String(index + 1) : name);
    });
    setIsEditing(true);
  };

  const onPositionNameChange = (index: number, name: string) => {
    let activeMarket = getActiveMarket();
    if (!activeMarket) {
      return;
    }
    SpotDesignationActions.updatePositionNames(
      activeMarket,
      index.toString(),
      name,
    );
  };

  const onSave = () => {
    props.savePositionMap();
    setIsEditing(false);
  };

  const onCancel = () => {
    props.fetchAll();
    setIsEditing(false);
    setSelectedRows(new Set());
  };

  const onHoverPositionName = (event: Event, index: number) => {
    setHoveredIndex(index);
  };

  const onLeavePositionName = () => {
    setHoveredIndex(null);
  };

  let { markets, filteredMarkets, positionMap, user } = props;
  if (!markets || !filteredMarkets || !positionMap) {
    return (
      <Page>
        {/* <Page.Header title="Stall Designation" /> */}
        <LoadingIndicator />
      </Page>
    );
  }
  if (markets.length === 0) {
    return (
      <Page style={styles.emptyMarket}>
        {/* <Page.Header title="Stall Designation" /> */}
        <Text>
          You have no markets. <Link to="/markets">Click Here</Link> to add a
          market.
        </Text>
      </Page>
    );
  }
  let activeMarket = getActiveMarket();
  let canEdit = user && canUser(user, { settings: 'WRITE' });
  return (
    <Page>
      <Page.Header title="Stall Designation" />
      <View>
        <View style={styles.control}>
          <View style={styles.dropdown}>
            {isEditing ? null : (
              <Dropdown
                options={filteredMarkets.getList()}
                value={activeMarket}
                onChange={onMarketSelect}
              />
            )}
          </View>
          {canEdit && renderButtonToolbar()}
        </View>
        {renderSpots()}
      </View>
    </Page>
  );
}

let styles = StyleSheet.create({
  control: {
    position: 'absolute',
    flexDirection: 'row',
    right: 0,
    zIndex: 1,
  },
  dropdown: {
    marginRight: 10,
  },
  emptyMarket: {
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
});

const connector = connectData()(
  () => {
    return {
      markets: { dataStoreKey: 'markets' },
      user: { dataStoreKey: 'user' },
      positionMap: { dataStoreKey: 'position' },
    };
  },
  () => ({
    savePositionMap: {
      dataStoreKey: 'position',
    },
  }),
  {
    computedProps: {
      filteredMarkets: {
        props: ['markets'],
        compute: (markets: IndexedList<Market> | null) => {
          if (!markets) {
            return null;
          }
          return markets.filter(
            (market) => market.dates != null && market.dates.length !== 0,
          );
        },
      },
    },
  },
);
type FullProps = ConnectedProps<typeof connector>;
export default connector(SpotDesignation);
